import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, AlertOctagon, Send, CheckCircle } from 'lucide-react';

const ReportThreat = () => {
  const [formData, setFormData] = useState({
    threatType: '',
    severity: '',
    description: '',
    email: '',
    evidence: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      <header className="border-b border-cyan-300/20 bg-gray-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-cyan-400" />
              <span className="text-xl font-bold text-white">CyberGuard Global</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2 text-gray-400 hover:text-cyan-400 transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <AlertOctagon className="h-16 w-16 text-orange-400 mx-auto mb-6" />
              <h1 className="text-5xl font-bold text-white mb-4">Report a Threat</h1>
              <p className="text-xl text-gray-300">Help us protect the community by reporting cybersecurity threats</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-300/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-3">Critical Threats</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Zero-day exploits</li>
                  <li>• Active malware campaigns</li>
                  <li>• Data breach attempts</li>
                  <li>• Ransomware attacks</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-300/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-3">Suspicious Activity</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Phishing campaigns</li>
                  <li>• Suspicious websites</li>
                  <li>• Malicious IP addresses</li>
                  <li>• Social engineering</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-300/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-3">AI Threats</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Deepfake content</li>
                  <li>• AI-generated scams</li>
                  <li>• Synthetic media</li>
                  <li>• Automated attacks</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8">
              <h2 className="text-3xl font-bold text-white mb-6">Submit Threat Report</h2>

              {submitted ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold">Threat report submitted successfully!</p>
                    <p className="text-gray-300 text-sm">Our security team will investigate within 24 hours.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-white mb-2">Threat Type *</label>
                    <select
                      name="threatType"
                      required
                      value={formData.threatType}
                      onChange={handleChange}
                      className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 transition-colors"
                    >
                      <option value="">Select threat type</option>
                      <option value="malware">Malware</option>
                      <option value="phishing">Phishing</option>
                      <option value="ransomware">Ransomware</option>
                      <option value="ddos">DDoS Attack</option>
                      <option value="data-breach">Data Breach</option>
                      <option value="deepfake">Deepfake/AI Threat</option>
                      <option value="surveillance">Surveillance Device</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-white mb-2">Severity Level *</label>
                    <select
                      name="severity"
                      required
                      value={formData.severity}
                      onChange={handleChange}
                      className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 transition-colors"
                    >
                      <option value="">Select severity</option>
                      <option value="critical">Critical - Active attack</option>
                      <option value="high">High - Immediate threat</option>
                      <option value="medium">Medium - Potential risk</option>
                      <option value="low">Low - Suspicious activity</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-white mb-2">Your Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 transition-colors"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2">Threat Description *</label>
                    <textarea
                      name="description"
                      required
                      value={formData.description}
                      onChange={handleChange}
                      rows="6"
                      className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                      placeholder="Provide detailed information about the threat, including URLs, IP addresses, file hashes, or other relevant details..."
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2">Evidence (URLs, IPs, File Hashes)</label>
                    <textarea
                      name="evidence"
                      value={formData.evidence}
                      onChange={handleChange}
                      rows="3"
                      className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                      placeholder="URLs, IP addresses, file hashes, screenshots, or other evidence..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-orange-500/50 transition-all flex items-center justify-center space-x-2"
                  >
                    <Send className="h-5 w-5" />
                    <span>Submit Threat Report</span>
                  </button>

                  <p className="text-sm text-gray-400 text-center">
                    All reports are reviewed by our security team within 24 hours
                  </p>
                </form>
              )}
            </div>

            <div className="mt-12 bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-3">What Happens Next?</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">1.</span>
                  <span>Our security team reviews your report within 24 hours</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">2.</span>
                  <span>We verify and analyze the threat using our multi-engine scanning</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">3.</span>
                  <span>If confirmed, the threat is added to our global intelligence database</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">4.</span>
                  <span>All users are automatically protected from this threat</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-cyan-300/20 bg-gray-900/50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-gray-400 text-sm">
            © 2024 CyberGuard Global. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReportThreat;
